import type {
  UserBadgeReputation,
  UserLanguages,
  UserOnlineStatus,
} from 'generated/openapi/types.gen';
import { Dot } from 'lucide-react';
import type { ComponentProps } from 'react';
import { memo, useMemo } from 'react';

import { ProfileLanguageFlags } from '@/features/profile/components/profileLanguageFlags';
import { ProfileNonToxicIcon } from '@/features/profile/components/profileNonToxicIcon';
import { ProfileUserBadges } from '@/features/profile/components/profileUserBadges';
import { useGetMyProfile } from '@/features/profile/hooks/useGetMyProfile';
import { getMutualLanguage } from '@/features/profile/utils/languages';
import { cn } from '@/shared/utils/cn';

export interface ProfileOverviewCardImageOverlayProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  name: string;
  age: number;
  bio?: string;
  languages: UserLanguages | undefined;
  onlineStatus: UserOnlineStatus;
  isReadyToPlay: boolean;
  userId: string;
  reputationBadge?: UserBadgeReputation;
  totalDuoGames?: number;
}

export const ProfileOverviewCardImageOverlay = memo(
  function ProfileOverviewCardImageOverlay({
    name,
    age,
    bio,
    languages,
    onlineStatus,
    isReadyToPlay,
    userId,
    reputationBadge,
    totalDuoGames,
    className,
    ...props
  }: ProfileOverviewCardImageOverlayProps) {
    const { data: myLanguages } = useGetMyProfile({
      options: { select: data => data.profile?.languages },
    });

    const mutualLanguage = useMemo(
      () => getMutualLanguage(myLanguages, languages),
      [myLanguages, languages],
    );

    return (
      <div
        className={cn(
          'absolute right-0 bottom-0 left-0 flex flex-col gap-2 rounded-b-[10px] bg-black/60 px-1 py-3 backdrop-blur-md',
          className,
        )}
        {...props}
      >
        {/* Badges row */}
        <div className="flex flex-wrap items-center justify-center gap-1">
          <ProfileUserBadges
            onlineStatus={onlineStatus}
            isReadyToPlay={isReadyToPlay}
            userId={userId}
            totalDuoGames={totalDuoGames}
            badgeVariant="glass"
          />
        </div>

        {/* Name row */}
        <div className="flex items-end justify-center gap-1">
          <ProfileNonToxicIcon
            tier={reputationBadge?.tier ?? 0}
            className="size-4"
          />
          <span className="font-nunito text-cold-100 text-base leading-none font-bold">
            {name}
          </span>
          <span className="font-nunito text-cold-400 text-sm leading-none font-normal">
            {age}
          </span>
          {mutualLanguage && (
            <>
              <Dot className="text-cold-400 size-4" />
              <ProfileLanguageFlags
                languageCodes={[mutualLanguage]}
                circleClassName="size-4"
              />
            </>
          )}
        </div>

        {/* Bio */}
        {bio && (
          <p className="text-cold-300 line-clamp-2 px-1 text-center text-sm leading-5 font-light italic">
            {bio}
          </p>
        )}
      </div>
    );
  },
);
